'use client';
// src/components/profile/RestaurantReviewSubmitForm.tsx
// Diner review form for Restaurant ("Craving") profiles. Open to anyone — no
// account required — with a 30-day per-guest rate limit. Text only: photos were
// removed because Firebase Storage uploads require auth, and the product has no
// customer sign-up.

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Star, Utensils, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { submitRestaurantReviewAction } from '@/app/actions/restaurantReviews';
import {
  RESTAURANT_REVIEW_CATEGORY_KEYS,
  RESTAURANT_REVIEW_TAGS,
} from '@/types/restaurantReview';
import type { RestaurantReviewCategoryKey, RestaurantReviewTag } from '@/types/restaurantReview';

interface RestaurantReviewSubmitFormProps {
  profileId: string;
  profileOwnerName: string;
  /** True if the current user owns this restaurant profile — they can see the section but not submit. */
  isOwner: boolean;
  /** Set when the signed-in diner has a confirmed/paid/completed booking on file (informational only; does not gate submission). */
  hasVerifiedBooking?: boolean;
}

const categoryLabels: Record<RestaurantReviewCategoryKey, string> = {
  food_quality: 'Food quality',
  service: 'Service',
  ambience: 'Ambience',
  value: 'Value',
  wait_time: 'Wait time',
};

const tagLabels: Record<RestaurantReviewTag, string> = {
  fresh_food: 'Fresh food',
  generous_portions: 'Generous portions',
  friendly_service: 'Friendly service',
  quick_service: 'Quick service',
  great_value: 'Great value',
  good_ambience: 'Good ambience',
};

export function RestaurantReviewSubmitForm({
  profileId,
  profileOwnerName,
  isOwner,
  hasVerifiedBooking = false,
}: RestaurantReviewSubmitFormProps) {
  const router = useRouter();

  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [body, setBody] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [categoryRatings, setCategoryRatings] = useState<Partial<Record<RestaurantReviewCategoryKey, number>>>({});
  const [tags, setTags] = useState<RestaurantReviewTag[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  if (isOwner) return null;

  const toggleTag = (tag: RestaurantReviewTag) => {
    setTags((current) =>
      current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag],
    );
  };

  const toggleCategoryRating = (key: RestaurantReviewCategoryKey, star: number) => {
    setCategoryRatings((current) => {
      const next = { ...current };
      if (next[key] === star) {
        delete next[key];
      } else {
        next[key] = star;
      }
      return next;
    });
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (rating === 0) {
      setResult({ success: false, message: 'Please choose a star rating.' });
      return;
    }
    if (!reviewerName.trim()) {
      setResult({ success: false, message: 'Please enter your first name.' });
      return;
    }
    setSubmitting(true);
    setResult(null);
    const res = await submitRestaurantReviewAction(profileId, {
      rating,
      body,
      reviewerName: reviewerName.trim(),
      categoryRatings,
      tags,
    });
    setResult(res);
    setSubmitting(false);
    if (res.success) {
      setRating(0);
      setBody('');
      setReviewerName('');
      setCategoryRatings({});
      setTags([]);
      router.refresh();
    }
  }

  if (result?.success) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800">
        <p className="flex items-center gap-2 font-semibold">
          <CheckCircle2 className="h-4 w-4" /> {result.message}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <p className="text-xs text-muted-foreground">
        Share your dining experience. Anyone can review a restaurant — no account needed. Reviews are
        public, so we automatically remove personal contact details.
      </p>

      {hasVerifiedBooking && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 text-xs text-emerald-800">
          <ShieldCheck className="h-4 w-4 shrink-0" />
          <span>
            Verified booking detected — your review will carry an extra trust signal.
          </span>
        </div>
      )}

      <div>
        <p className="mb-1 text-sm font-medium text-foreground">Your rating for {profileOwnerName}</p>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              className="focus:outline-none"
              aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
            >
              <Star
                className={`h-7 w-7 transition-colors ${
                  star <= (hovered || rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="restaurant-reviewer-name">Your first name *</Label>
        <Input
          id="restaurant-reviewer-name"
          placeholder="e.g. Thabo M"
          value={reviewerName}
          onChange={(e) => setReviewerName(e.target.value)}
          maxLength={40}
          required
        />
      </div>

      <div className="rounded-lg border border-dashed border-slate-300 p-3">
        <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <Utensils className="h-4 w-4 text-orange-500" />
          Rate the details (optional)
        </p>
        <div className="mt-3 space-y-3">
          {RESTAURANT_REVIEW_CATEGORY_KEYS.map((key) => (
            <div key={key} className="flex items-center justify-between gap-4 text-sm">
              <span className="text-muted-foreground">{categoryLabels[key]}</span>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => toggleCategoryRating(key, star)}
                    className="focus:outline-none"
                    aria-label={`${categoryLabels[key]} ${star} star${star > 1 ? 's' : ''}`}
                  >
                    <Star
                      className={`h-5 w-5 transition-colors ${
                        star <= (categoryRatings[key] || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-slate-300 p-3">
        <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">What stood out? (optional)</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {RESTAURANT_REVIEW_TAGS.map((tag) => (
            <label key={tag} className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={tags.includes(tag)}
                onChange={() => toggleTag(tag)}
                disabled={submitting}
              />
              <span>{tagLabels[tag]}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="restaurant-review-body">Your review</Label>
        <Textarea
          id="restaurant-review-body"
          placeholder="What was the food, service, and atmosphere like?"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={500}
          rows={4}
          required
        />
        <p className="text-right text-xs text-muted-foreground">{body.length}/500</p>
      </div>

      {result && !result.success && <p className="text-sm text-destructive">{result.message}</p>}

      <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
        {submitting ? 'Submitting...' : 'Submit Diner Review'}
      </Button>
    </form>
  );
}
